'use client'

import { useEffect, useRef } from 'react'

import { usePathname } from 'next/navigation'

import { extractTrackingProps, initAudit, cleanupAudit, logEvent } from '@/lib/analytics/audit'
import type { BeaconPayload } from '@/lib/analytics/beacon-transport'
import { sendBeaconPayload } from '@/lib/analytics/beacon-transport'
import { maybeReloadForChunkError } from '@/lib/analytics/chunk-reload'
import { COMPONENT_EXPOSURE_EVENT, readExperimentComponents } from '@/lib/analytics/component-experiments'
import { readEngagedSeconds, resetEngagedTime, startEngagedTime } from '@/lib/analytics/engaged-time'
import { shouldReportError } from '@/lib/analytics/error-limit'
import type { TrackedEvent } from '@/lib/analytics/events'
import { EventName, EventType } from '@/lib/analytics/events'
import { readExperimentAssignments, recordExperimentExposure } from '@/lib/analytics/experiment-exposures'
import {
  encodeExperimentAssignments,
  formatExperimentAssignments,
  type ExperimentAssignments,
} from '@/lib/analytics/experiment-tracking'
import { hydrationContext } from '@/lib/analytics/hydration-context'
import { buildLcpEvent, captureLcp, type LcpPageContext } from '@/lib/analytics/lcp'
import {
  isNewsletterMedium,
  NEWSLETTER_QUALIFICATION_PROPERTY,
  NEWSLETTER_QUALIFIED_PROPERTY,
  NEWSLETTER_UNQUALIFIED_ROLLUP_KEY,
  type NewsletterQualificationReason,
} from '@/lib/analytics/newsletter-source'
import { buildClientPageviewProps } from '@/lib/analytics/pageview-props'
import { describeRejection } from '@/lib/analytics/rejection-reason'
import { shouldSampleImpression, getImpressionSampleRate, isSamplingExempt } from '@/lib/analytics/sampling'
import { getSessionContext, isSessionActive } from '@/lib/analytics/session-id'
import { resolveTrackingPosition } from '@/lib/analytics/tracking-position'
import { normalizeTrackingProperties } from '@/lib/analytics/tracking-props'
import { utmize } from '@/lib/analytics/utmize'
import type { PageType } from '@/types/nav-types'

// Any intersection counts, and it counts immediately — this matches ../next,
// whose observer took no options (threshold defaults to 0) and fired on the
// first callback. A stricter rule (50% visible for 500ms, closer to the IAB
// viewability standard) was tried and undercounted impressions against the
// historical series advertisers are reported on.
const IMPRESSION_THRESHOLD = 0
// Deliberately tracker-token-free path — see src/app/api/ht/route.ts.
const BEACON_PATH = '/api/ht'
// The scanner cohort recorded zero focused/visible engaged time, while real
// campaign readers separated cleanly at this small threshold. It is long
// enough to reject link previews without delaying a real reader's first
// periodic/terminal flush in any meaningful way.
const NEWSLETTER_DWELL_SECONDS = 3

// Reported `distinct_id` for a visitor with no readable `ph_distinct_id` cookie
// (bots, cookie-blocked browsers). Deliberately a shared constant so these hits
// don't mint a PostHog person each.
const ANONYMOUS_DISTINCT_ID = 'anonymous'

let moduleState: EngineState | null = null

// Visibility at the moment this client bundle first evaluated. A tab opened in
// the background (open-in-new-tab, a restored session, a link with target
// _blank that stays behind) runs its JS while hidden — so this is our most
// portable "was this a background load?" signal. It matters because the
// render-time duration is `Date.now() - proxyArrivalStamp`, and a background
// tab's timers/effects are deferred/throttled, so that subtraction folds in the
// entire idle gap (we've seen 10-hour "renders"). The Page Visibility
// performance timeline used in wasBackgroundedSinceLoad only exists on Chromium.
const initiallyHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'

// True if the tab was hidden at any point between navigation and now, meaning an
// idle background gap is baked into any elapsed-time measurement taken here. We
// TAG rather than drop the sample (accurate at ingest; reporting filters it) so
// a genuinely slow render is never silently lost — only demoted to "suspect".
const wasBackgroundedSinceLoad = (): boolean => {
  if (typeof document === 'undefined') {
    return false
  }
  if (initiallyHidden || document.visibilityState === 'hidden') {
    return true
  }
  try {
    // Chromium keeps a visibility-state timeline since navigation start; any
    // 'hidden' entry means the tab was backgrounded before we sampled.
    return performance.getEntriesByType('visibility-state').some((e) => e.name === 'hidden')
  } catch {
    return false
  }
}

interface EngineState {
  queue: TrackedEvent[]
  allEvents: TrackedEvent[]
  rollup: Record<string, number>
  impressionSeen: WeakSet<Element>
  clickSeen: WeakSet<Element>
  intersectionObserver: IntersectionObserver | null
  mutationObserver: MutationObserver | null
  distinctId: string
  segment: string
  experiments: ExperimentAssignments
  components: string[]
  fontVariant: string
  // The current route's page type, kept current across SPA navigations by the
  // hook. Stamped on every event so an A/B scope can select a whole page type.
  pageType: PageType
  requestId: string
  locale: string
  pageViewTime: number
  // Identity + URL of the pageview currently being observed — captured at
  // emit time so the matching `$pageleave` reports the page being left, not
  // wherever an SPA navigation has already moved to.
  pageSessionId: string
  pageWindowId: string
  pageViewPath: string
  pageViewUrl: string
  pageViewHost: string
  // Scroll depth (0..1) for the current page, fed into `$pageleave`.
  maxScrollPct: number
  lastScrollPct: number
  // Guards a single `$pageleave` per pageview across hidden/unload/route-change.
  pageLeaveSent: boolean
  // Same guard for `time-on-page` (emitted alongside `$pageleave`).
  timeOnPageSent: boolean
  newsletterGate: {
    active: boolean
    humanQualified: boolean
    pageview: TrackedEvent | null
  }
}

export type { BeaconPayload }

const buildEventFromElement = (el: HTMLElement, eventType: EventType): TrackedEvent | null => {
  const eventName = el.dataset.eventName
  if (!eventName) {
    return null
  }

  const props = extractTrackingProps(el)
  const path = typeof window !== 'undefined' ? window.location.pathname : ''

  const properties: Record<string, string | number | boolean | undefined> = {
    ...normalizeTrackingProperties(props, { eventName }),
    action: eventType,
    path,
  }

  if (el instanceof HTMLAnchorElement) {
    properties.link = el.href
  } else if (props.eventUrl && properties.link === undefined) {
    properties.link = props.eventUrl
  }

  return {
    event: eventName.includes('--') ? eventName : `${eventName}--${eventType}`,
    eventType,
    properties,
    timestamp: Date.now(),
  }
}

const qualifyNewsletterGate = (state: EngineState, reason: NewsletterQualificationReason) => {
  const gate = state.newsletterGate
  if (!gate.active || gate.humanQualified) {
    return
  }
  gate.humanQualified = true
  if (gate.pageview) {
    gate.pageview.properties[NEWSLETTER_QUALIFIED_PROPERTY] = true
    gate.pageview.properties[NEWSLETTER_QUALIFICATION_PROPERTY] = reason
  }
}

const clearBeaconQueue = (state: EngineState) => {
  state.queue = []
  state.allEvents = []
  state.rollup = {}
}

const rollupKey = (event: TrackedEvent): string => `${event.event}:${event.eventType}`

const isErrorEvent = (event: TrackedEvent): boolean => event.eventType === EventType.Error

// A withheld newsletter queue still releases two things.
//
// Client errors, because they're diagnostics rather than audience metrics — and
// a landing that bounces before it qualifies is exactly when we most want the
// error that caused it. Scanner error noise doesn't ride in on this: the
// SafeLinks/Defender signature is already dropped at capture (error-limit.ts).
//
// And, at a real page end, a single rollup counter, so the suppressed cohort
// stays countable in `analytics_event_daily` without a PostHog event or a
// site_events row. A payload with no events carries no newsletter attribution
// for the route's exact-UA backstop to match, so the counter survives it.
const releaseWithheldNewsletterQueue = (state: EngineState, terminal: boolean) => {
  const errors = state.allEvents.filter(isErrorEvent)
  if (errors.length === 0 && !terminal) {
    return
  }

  const counts: Record<string, number> = {}
  for (const event of errors) {
    counts[rollupKey(event)] = (counts[rollupKey(event)] ?? 0) + 1
  }
  if (terminal) {
    counts[NEWSLETTER_UNQUALIFIED_ROLLUP_KEY] = 1
  }

  sendBeaconPayload(BEACON_PATH, {
    events: state.queue.filter(isErrorEvent),
    allEvents: errors,
    rollup: {
      counts,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      timestamp: Date.now(),
    },
    requestId: state.requestId,
    distinctId: state.distinctId,
    locale: state.locale,
    sentAt: Date.now(),
  })

  if (terminal) {
    clearBeaconQueue(state)
    return
  }

  // The errors are away; the rest of the queue stays put in case a background
  // tab later becomes a real reading session. `state.rollup` only ever holds
  // the currently queued events (every flush resets it), so rebuilding it from
  // what's left is what keeps the counts from being sent twice.
  state.queue = state.queue.filter((event) => !isErrorEvent(event))
  state.allEvents = state.allEvents.filter((event) => !isErrorEvent(event))
  state.rollup = {}
  for (const event of state.allEvents) {
    state.rollup[rollupKey(event)] = (state.rollup[rollupKey(event)] ?? 0) + 1
  }
}

const flushBeacon = (state: EngineState, terminal = false) => {
  if (state.queue.length === 0 && state.allEvents.length === 0 && Object.keys(state.rollup).length === 0) {
    return
  }

  if (state.newsletterGate.active && !state.newsletterGate.humanQualified) {
    if (readEngagedSeconds() >= NEWSLETTER_DWELL_SECONDS) {
      qualifyNewsletterGate(state, 'dwell')
    }
    if (!state.newsletterGate.humanQualified) {
      // A periodic/visibility flush retains the queue in case a background tab
      // later becomes a real reading session. A true page end discards it so a
      // link preview never reaches either analytics sink — leaving behind only
      // the errors and the drop counter.
      releaseWithheldNewsletterQueue(state, terminal)
      return
    }
  }

  const payload: BeaconPayload = {
    events: state.queue,
    allEvents: state.allEvents,
    rollup: {
      counts: { ...state.rollup },
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      timestamp: Date.now(),
    },
    requestId: state.requestId,
    distinctId: state.distinctId,
    locale: state.locale,
    sentAt: Date.now(),
    ...(state.newsletterGate.active ? { newsletter: { humanQualified: true } } : {}),
  }

  // Reset up front: the send below is best-effort and fire-and-forget, so a
  // throw from a browser API the environment has disabled must NEVER skip the
  // reset — that's what let the queue grow and re-throw on every flush.
  clearBeaconQueue(state)

  // Handles the disabled-Blob / no-sendBeacon environments and splits a batch
  // the browser refuses for size. See beacon-transport.ts.
  sendBeaconPayload(BEACON_PATH, payload)
}

const attachSessionContext = (tracked: TrackedEvent, state: EngineState): boolean => {
  if (!tracked.properties.$session_id && state.pageSessionId) {
    tracked.properties.$session_id = state.pageSessionId
    tracked.properties.$window_id = state.pageWindowId
    tracked.properties.$current_url = state.pageViewUrl
    tracked.properties.$host = state.pageViewHost
  }

  const sessionId = tracked.properties.$session_id
  if (typeof sessionId === 'string' && isSessionActive(sessionId)) {
    return true
  }
  // A timer, deferred LCP, popup abandon or unload from a parked tab must not
  // extend its old session. Keep diagnostics/business events without a session;
  // discard stale leave durations, which include the entire idle gap.
  delete tracked.properties.$session_id
  delete tracked.properties.$window_id
  return tracked.event !== '$pageleave' && tracked.event !== 'time-on-page'
}

const processEvent = (tracked: TrackedEvent, state: EngineState) => {
  if (!attachSessionContext(tracked, state)) {
    return
  }
  const rollupKey = `${tracked.event}:${tracked.eventType}`
  state.rollup[rollupKey] = (state.rollup[rollupKey] ?? 0) + 1

  state.allEvents.push(tracked)
  tracked.properties.segment = state.segment
  if (state.components.length) {
    tracked.properties.abComponents = state.components.join(',')
  }
  const experimentTokens = encodeExperimentAssignments(state.experiments)
  if (experimentTokens) {
    tracked.properties.experiments = experimentTokens
    tracked.properties.testVariant = formatExperimentAssignments(state.experiments)
  }
  // Stamped on every event (mirrors segment) so the FONT_STRATEGY=ab test can
  // compare conversion by font. Constant ('inter'/'system') outside ab mode.
  tracked.properties.fontVariant = state.fontVariant
  // Lets an experiment scope a cohort to a page type (product pages share the
  // /hearing-aids/ prefix with brand hubs and articles). See ab-tests/filters.ts.
  tracked.properties.pageType = state.pageType

  // Sampling-exempt impressions (e.g. gsc-show) are treated like non-impressions:
  // always sent and NOT flagged `sampled`, so admin queries count them at 1x.
  if (tracked.eventType === EventType.Impression && !isSamplingExempt(tracked.event)) {
    const sampled = shouldSampleImpression(tracked.event)
    logEvent(tracked, sampled)
    if (sampled) {
      tracked.properties.sampled = true
      tracked.properties.sampleRate = getImpressionSampleRate()
      state.queue.push(tracked)
    }
  } else {
    logEvent(tracked, true)
    state.queue.push(tracked)
  }
}

const onIntersection =
  (state: EngineState): IntersectionObserverCallback =>
  (entries) => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement

      if (!entry.isIntersecting || state.impressionSeen.has(el)) {
        continue
      }
      // `checkVisibility()` catches the display:none half of a responsive pair
      // (the desktop and mobile ad variants are both in the DOM).
      const isVisible = !el.checkVisibility || el.checkVisibility()
      if (!isVisible) {
        continue
      }

      state.impressionSeen.add(el)
      state.intersectionObserver?.unobserve(el)

      const tracked = buildEventFromElement(el, EventType.Impression)
      if (tracked) {
        processEvent(tracked, state)
      }
    }
  }

const observeElement = (el: Element, state: EngineState) => {
  if (state.impressionSeen.has(el)) {
    return
  }
  state.intersectionObserver?.observe(el)
}

const scanForTrackables = (root: Element | Document, state: EngineState) => {
  root.querySelectorAll('[data-track-impression]').forEach((el) => {
    observeElement(el, state)
  })
}

const parseLegacyTracking = (raw: string | null): Record<string, string> => {
  if (!raw) return {}
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    // Keep the network-returned value if it is not valid percent encoding.
  }

  const fields: Record<string, string> = {}
  for (const part of decoded.split('|')) {
    const separator = part.indexOf('=')
    if (separator > 0) {
      fields[part.slice(0, separator)] = part.slice(separator + 1)
    }
  }
  return fields
}

const legacyTrackingFromUrl = (url: URL): Record<string, string> => {
  const queryValue =
    url.searchParams.get('sub3') ??
    url.searchParams.get('subId3') ??
    url.searchParams.get('utm_search') ??
    url.searchParams.get('clickref1') ??
    url.searchParams.get('sid')
  if (queryValue) return parseLegacyTracking(queryValue)

  const pubref = /\/pubref:([^/]+)/.exec(url.pathname)?.[1] ?? null
  return parseLegacyTracking(pubref)
}

// Server-rendered links use session sentinel "0" and sometimes share one base
// URL across responsive CTA variants. Immediately before navigation, refresh
// the URL with the live session, actual clicked position, and current date.
// This also upgrades a legacy link from an older cached page to compact format;
// Audien remains legacy by network policy but gets a truthful click date.
const refreshAffiliateHref = (el: HTMLElement, state: EngineState, pageType: PageType): void => {
  const authoredAffiliate = el.dataset.affiliateReference === 'true'
  if (
    !(el instanceof HTMLAnchorElement) ||
    el.dataset.tracking === 'ignore' ||
    (!authoredAffiliate &&
      el.dataset.eventName !== EventName.AffiliateAction &&
      el.dataset.eventName !== 'table-affiliate-visit')
  ) {
    return
  }

  let currentUrl: URL
  try {
    currentUrl = new URL(el.href)
  } catch {
    return
  }
  const legacy = legacyTrackingFromUrl(currentUrl)
  // Another tab may have exposed this visitor since this page loaded. Read at
  // navigation time so the outbound link and its click event agree.
  state.experiments = readExperimentAssignments(state.distinctId)
  const href = utmize({
    url: el.href,
    pathname: window.location.pathname,
    search: window.location.search,
    content: resolveTrackingPosition(el),
    release: authoredAffiliate ? undefined : (el.dataset.eventProduct ?? el.dataset.eventRelease),
    sessionId: state.pageSessionId,
    experiments: state.experiments,
    pageType,
    segment: legacy.v ?? state.segment,
    campaign: legacy.ca,
    medium: authoredAffiliate ? 'button' : (legacy.m ?? 'button'),
    preserveCampaignParameters: authoredAffiliate,
    term: currentUrl.searchParams.get('sub4') ?? currentUrl.searchParams.get('sharedId') ?? legacy.t ?? 'lead-term',
  })
  el.href = href
  el.dataset.eventUrl = href
}

const startImpressionObserver = (state: EngineState) => {
  state.intersectionObserver?.disconnect()
  state.intersectionObserver = new IntersectionObserver(onIntersection(state), {
    threshold: IMPRESSION_THRESHOLD,
  })
  scanForTrackables(document, state)
}

// Fraction of the page scrolled into view (0..1). A page that fits the viewport
// counts as fully seen (1), matching how PostHog treats unscrollable pages.
const computeScrollPct = (): number => {
  const doc = document.documentElement
  const scrollable = doc.scrollHeight - doc.clientHeight
  if (scrollable <= 0) {
    return 1
  }
  return Math.min(1, Math.max(0, window.scrollY / scrollable))
}

const refreshComponentExperiments = (state: EngineState) => {
  if (state.pageViewPath !== window.location.pathname) {
    return
  }
  const components = readExperimentComponents(state.pageViewPath)
  if (!components.some((component) => !state.components.includes(component))) {
    return
  }
  state.components = components
  state.experiments = recordExperimentExposure({
    visitorId: state.distinctId,
    pathname: state.pageViewPath,
    pageType: state.pageType,
    segment: state.segment,
    components,
  })
  processEvent(
    {
      event: COMPONENT_EXPOSURE_EVENT,
      eventType: EventType.AbTest,
      properties: buildClientPageviewProps({
        sessionId: state.pageSessionId,
        windowId: state.pageWindowId,
        deviceId: state.distinctId,
        segment: state.segment,
      }),
      timestamp: Date.now(),
    },
    state
  )
}

const emitPageView = (state: EngineState, context = getSessionContext()) => {
  state.components = []
  state.pageViewTime = Date.now()
  state.pageLeaveSent = false
  state.timeOnPageSent = false
  // Engaged time is per-pageview, and an SPA route change reuses this engine —
  // so the counter restarts here rather than only on mount.
  resetEngagedTime()
  state.maxScrollPct = computeScrollPct()
  state.lastScrollPct = state.maxScrollPct

  const { sessionId, windowId } = context
  state.pageSessionId = sessionId
  state.pageWindowId = windowId
  state.pageViewPath = window.location.pathname
  state.pageViewUrl = window.location.href
  state.pageViewHost = window.location.host
  state.experiments = recordExperimentExposure({
    visitorId: state.distinctId,
    pathname: state.pageViewPath,
    pageType: state.pageType,
    segment: state.segment,
    now: state.pageViewTime,
  })

  const properties = buildClientPageviewProps({
    sessionId,
    windowId,
    deviceId: state.distinctId,
    segment: state.segment,
  })
  const medium = properties.utm_medium
  state.newsletterGate = {
    // Campaign params were already parsed for the pageview property bag, so
    // this adds only a string check to the non-newsletter path.
    active: typeof medium === 'string' && isNewsletterMedium(medium),
    humanQualified: false,
    pageview: null,
  }

  const tracked: TrackedEvent = {
    event: EventName.PageView,
    eventType: EventType.Performance,
    properties,
    timestamp: state.pageViewTime,
  }
  if (state.newsletterGate.active) {
    state.newsletterGate.pageview = tracked
  }
  processEvent(tracked, state)
  refreshComponentExperiments(state)
}

// `$pageleave` is what PostHog Web Analytics uses for bounce rate, session
// duration and scroll depth — it carries the same `$session_id` as the pageview
// plus `$prev_pageview_*` rollups. Emitted once per pageview, on the first of
// tab-hidden / unload / SPA route change.
const emitPageLeave = (state: EngineState) => {
  if (state.pageLeaveSent || !state.pageViewTime) {
    return
  }
  state.pageLeaveSent = true

  const tracked: TrackedEvent = {
    event: '$pageleave',
    eventType: EventType.Performance,
    properties: {
      $session_id: state.pageSessionId,
      $window_id: state.pageWindowId,
      $device_id: state.distinctId,
      $current_url: state.pageViewUrl,
      $host: state.pageViewHost,
      $pathname: state.pageViewPath,
      $prev_pageview_pathname: state.pageViewPath,
      $prev_pageview_duration: (Date.now() - state.pageViewTime) / 1000,
      // Ours, not PostHog's — the focus/idle-aware counterpart to the wall-clock
      // duration above. Both ship so reporting can compare them; see
      // lib/analytics/engaged-time.ts for why wall-clock alone misleads.
      engagedDuration: readEngagedSeconds(),
      $prev_pageview_last_scroll_percentage: state.lastScrollPct,
      $prev_pageview_max_scroll_percentage: state.maxScrollPct,
      // site_events path column
      path: state.pageViewPath,
      segment: state.segment,
    },
    timestamp: Date.now(),
  }
  processEvent(tracked, state)
}

// Emit on the initial document load only. Reads the proxy-arrival timestamp +
// path the middleware stamped into the `ht_render` cookie (proxy.ts →
// finalizeResponse) and computes proxy-arrival → render duration. We read the
// cookie, NOT the `Server-Timing` header: Vercel strips arbitrary headers from
// prerender-cache hits but keeps Set-Cookie, so the header is absent on cached
// pages (which is most of them). SPA navigations don't re-run this — the engine
// only calls it on initial load.
const emitPageRenderTime = (state: EngineState) => {
  const stamp = readRenderStart()
  if (!stamp) {
    return
  }
  if (stamp.path !== window.location.pathname) {
    // A concurrent load to a different path overwrote the (origin-global) cookie
    // before we read it — drop the sample rather than attribute the wrong
    // duration to this page.
    return
  }
  const duration = Date.now() - stamp.start
  if (duration < 0) {
    // Negative means clock skew (server clock ahead of client). Skip the sample
    // rather than write a meaningless number.
    return
  }
  // TTFB separates a cache serve (low — tens of ms) from an origin render / cache
  // MISS (high). Vercel's x-vercel-cache header can't be read from JS — it's set
  // by the CDN downstream of middleware and the navigation entry doesn't expose
  // response headers — so navigation TTFB is the client-side proxy for hit-vs-miss.
  const nav =
    typeof performance !== 'undefined'
      ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
      : undefined
  const ttfb = nav && Number.isFinite(nav.responseStart) ? Math.round(nav.responseStart) : undefined
  // Tag (don't drop) samples where the tab was backgrounded during load — the
  // duration includes idle time, so reporting excludes these from averages.
  const backgrounded = wasBackgroundedSinceLoad()
  const tracked: TrackedEvent = {
    event: 'page-render-time',
    eventType: EventType.Performance,
    properties: {
      path: window.location.pathname,
      duration,
      ...(ttfb !== undefined ? { ttfb } : {}),
      ...(backgrounded ? { backgrounded: true } : {}),
    },
    timestamp: Date.now(),
  }
  processEvent(tracked, state)
}

const emitTimeOnPage = (state: EngineState) => {
  if (state.timeOnPageSent || !state.pageViewTime) {
    return
  }
  state.timeOnPageSent = true
  const duration = Date.now() - state.pageViewTime
  const tracked: TrackedEvent = {
    event: 'time-on-page',
    eventType: EventType.Performance,
    properties: {
      path: window.location.pathname,
      duration,
    },
    timestamp: Date.now(),
  }
  processEvent(tracked, state)
}

const emitError = (message: string, extra: Record<string, string | undefined>, state: EngineState) => {
  if (!shouldReportError(message)) {
    return
  }
  const tracked: TrackedEvent = {
    event: 'client-error',
    eventType: EventType.Error,
    properties: {
      path: window.location.pathname,
      message,
      ...extra,
    },
    timestamp: Date.now(),
  }
  processEvent(tracked, state)
  maybeReloadForChunkError(message)
}

const resetForNewPage = (state: EngineState) => {
  emitPageLeave(state)
  emitTimeOnPage(state)
  flushBeacon(state, true)
  state.impressionSeen = new WeakSet()
  state.clickSeen = new WeakSet()
}

const refreshPageSession = (state: EngineState) => {
  if (document.visibilityState !== 'visible') {
    return
  }
  const context = getSessionContext()
  if (context.sessionId === state.pageSessionId) {
    return
  }
  // End the old page/gate before starting a new visit on the same URL. In
  // particular, a returning newsletter reader must not release an old queue
  // under the new visit's qualification or session identity.
  resetForNewPage(state)
  emitPageView(state, context)
  startImpressionObserver(state)
}

const readRequestId = (): string => {
  // Set per-request by the proxy (proxy.ts → finalizeResponse). Cookie-based so
  // the server-rendered HTML stays cacheable; reading the ID via headers() in a
  // server component would force the route dynamic.
  const match = document.cookie.split('; ').find((c) => c.startsWith('ht_request_id='))
  return match?.split('=')[1] ?? ''
}

// Reads the proxy's per-request render stamp (`ht_render` = `<unix-ms>|<path>`,
// set in proxy.ts → finalizeResponse). Tolerates whether the cookie value was
// percent-encoded in transit. Returns null when absent/malformed.
const readRenderStart = (): { start: number; path: string } | null => {
  const match = document.cookie.split('; ').find((c) => c.startsWith('ht_render='))
  if (!match) {
    return null
  }
  let value = match.slice('ht_render='.length)
  try {
    value = decodeURIComponent(value)
  } catch {
    // Not percent-encoded — use the raw value.
  }
  const sep = value.indexOf('|')
  if (sep < 0) {
    return null
  }
  const start = parseInt(value.slice(0, sep), 10)
  if (!Number.isFinite(start)) {
    return null
  }
  return { start, path: value.slice(sep + 1) }
}

const readLocale = (): string => {
  return document.documentElement.lang || 'en'
}

const lcpPageContext = (state: EngineState): LcpPageContext => ({
  path: state.pageViewPath,
  url: state.pageViewUrl,
  host: state.pageViewHost,
  pageType: state.pageType,
  requestId: state.requestId,
  sessionId: state.pageSessionId,
  windowId: state.pageWindowId,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
})

export const useAnalyticsEngine = ({
  distinctId,
  segment,
  fontVariant,
  pageType,
}: {
  /** The `ph_distinct_id` cookie, or undefined when it isn't readable.
   *  Must be stable across renders — a change tears down and re-runs the
   *  engine, which double-emits `$pageview`. */
  distinctId: string | undefined
  segment: string
  fontVariant: string
  pageType: PageType
}) => {
  const stateRef = useRef<EngineState | null>(null)
  const pageTypeRef = useRef(pageType)
  pageTypeRef.current = pageType
  const pathname = usePathname()
  const prevPathnameRef = useRef<string | null>(null)

  useEffect(() => {
    const state: EngineState = {
      queue: [],
      allEvents: [],
      rollup: {},
      impressionSeen: new WeakSet(),
      clickSeen: new WeakSet(),
      intersectionObserver: null,
      mutationObserver: null,
      distinctId: distinctId ?? ANONYMOUS_DISTINCT_ID,
      segment,
      experiments: {},
      components: [],
      fontVariant,
      pageType: pageTypeRef.current,
      requestId: readRequestId(),
      locale: readLocale(),
      pageViewTime: 0,
      pageSessionId: '',
      pageWindowId: '',
      pageViewPath: '',
      pageViewUrl: '',
      pageViewHost: '',
      maxScrollPct: 0,
      lastScrollPct: 0,
      pageLeaveSent: false,
      timeOnPageSent: false,
      newsletterGate: { active: false, humanQualified: false, pageview: null },
    }
    stateRef.current = state
    moduleState = state

    initAudit()
    // Before emitPageView: that call resets the counter, and the listeners must
    // already be attached so activity on the first page is not missed.
    // The activity callback is wired unconditionally — `qualifyNewsletterGate`
    // no-ops unless the CURRENT pageview is newsletter-attributed, and only
    // emitPageView knows that. Selecting the callback here from the landing
    // page would leave a gate that an SPA route change turned on with no way to
    // qualify but dwell.
    let lastSessionActivity = 0
    const stopEngagedTime = startEngagedTime(() => {
      const now = Date.now()
      // Scroll/wheel input can arrive every frame. Touch shared storage at
      // most once per second; passive analytics flushes never touch it.
      if (now < lastSessionActivity || now - lastSessionActivity >= 1_000) {
        refreshPageSession(state)
        lastSessionActivity = now
      }
      qualifyNewsletterGate(state, 'activity')
    })
    startImpressionObserver(state)
    emitPageView(state)
    emitPageRenderTime(state)
    // LCP belongs to the document navigation, even if its final report arrives
    // after an SPA transition. BFCache restores start a new metric at the
    // restored URL. Keep the original page identity for ordinary navigations.
    const documentPage = lcpPageContext(state)
    const stopLcp = captureLcp((metric) => {
      const page = metric.navigationType === 'back-forward-cache' ? lcpPageContext(state) : documentPage
      processEvent(buildLcpEvent(metric, page), { ...state, pageType: page.pageType })
      // The metric can arrive after our visibility/unload flush. Send it now
      // so a short visit is represented without waiting for the 30s interval.
      flushBeacon(state)
    })
    prevPathnameRef.current = pathname

    state.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          if (mutation.target.dataset.trackImpression !== undefined) {
            observeElement(mutation.target, state)
          }
        }
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            if (node.dataset.trackImpression !== undefined) {
              observeElement(node, state)
            }
            scanForTrackables(node, state)
          }
        }
      }
      refreshComponentExperiments(state)
    })

    state.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-track-impression'],
    })

    const onClick = (e: MouseEvent) => {
      let el = e.target as HTMLElement | null
      while (el && el !== document.body) {
        if (el.dataset.track !== undefined) {
          refreshPageSession(state)
          refreshAffiliateHref(el, state, pageTypeRef.current)
          if (!state.clickSeen.has(el)) {
            state.clickSeen.add(el)
            const tracked = buildEventFromElement(el, EventType.Click)
            if (tracked) {
              processEvent(tracked, state)
            }
          }
          break
        }
        el = el.parentElement
      }
    }

    document.addEventListener('click', onClick)

    // Refresh before middle-click/new-tab/context-menu navigation as well as
    // ordinary clicks. Merely opening the menu must not count as a conversion.
    const onAffiliateIntent = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-track]') : null
      if (el) {
        refreshPageSession(state)
        refreshAffiliateHref(el, state, pageTypeRef.current)
      }
    }
    const onAuxClick = (e: MouseEvent) => {
      if (e.button === 1) {
        onClick(e)
      }
    }
    document.addEventListener('pointerdown', onAffiliateIntent)
    document.addEventListener('contextmenu', onAffiliateIntent)
    document.addEventListener('auxclick', onAuxClick)

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') {
        refreshPageSession(state)
        return
      }
      // `$pageleave` / `time-on-page` fire at most once per pageview (both are
      // internally guarded). Brief tab returns do not re-arm: a visitor who
      // tab-switches repeatedly was emitting a fresh `$pageleave` + `time-on-page`
      // on every hide, so pageleaves outnumbered pageviews. Duration is measured
      // to the first hide. `flushBeacon` still runs on every hide so queued
      // impressions aren't lost if the tab is killed while backgrounded.
      emitPageLeave(state)
      emitTimeOnPage(state)
      flushBeacon(state)
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    const onResume = () => refreshPageSession(state)
    window.addEventListener('focus', onResume)
    window.addEventListener('pageshow', onResume)

    const onScroll = () => {
      const pct = computeScrollPct()
      state.lastScrollPct = pct
      if (pct > state.maxScrollPct) {
        state.maxScrollPct = pct
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    const onError = (e: ErrorEvent) => {
      emitError(
        e.message,
        {
          filename: e.filename,
          lineno: String(e.lineno),
          colno: String(e.colno),
          stack: e.error?.stack,
          // Hydration mismatches arrive here rather than at the error boundary,
          // so they carry no componentStack — this adds the DOM state instead.
          // No-op for every other error.
          ...hydrationContext(e.message),
        },
        state
      )
    }

    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      // A rejection with nothing to report (bare `Promise.reject()`, an empty
      // object) is dropped rather than logged as "undefined"/"[object Object]".
      const report = describeRejection(e.reason)
      if (!report) {
        return
      }
      emitError(report.message, { source: 'unhandledrejection', stack: report.stack }, state)
    }

    const onBeforeUnload = () => {
      emitPageLeave(state)
      emitTimeOnPage(state)
      flushBeacon(state, true)
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('beforeunload', onBeforeUnload)

    const flushInterval = setInterval(() => flushBeacon(state), 30_000)

    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('pointerdown', onAffiliateIntent)
      document.removeEventListener('contextmenu', onAffiliateIntent)
      document.removeEventListener('auxclick', onAuxClick)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onResume)
      window.removeEventListener('pageshow', onResume)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('beforeunload', onBeforeUnload)
      clearInterval(flushInterval)
      stopLcp()
      state.intersectionObserver?.disconnect()
      state.mutationObserver?.disconnect()
      // After emitPageLeave below would drop the final page's engaged time, so
      // the teardown order matters: read first, unhook after.
      emitPageLeave(state)
      emitTimeOnPage(state)
      flushBeacon(state, true)
      stopEngagedTime()
      cleanupAudit()
      stateRef.current = null
      moduleState = null
    }
  }, [distinctId, segment, fontVariant])

  // The engine effect above deliberately does not re-run on pageType (that
  // would double-emit `$pageview`), so keep the stamped value current here.
  useEffect(() => {
    if (stateRef.current) {
      stateRef.current.pageType = pageType
    }
  }, [pageType])

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return
    }
    prevPathnameRef.current = pathname

    const state = stateRef.current
    if (!state) {
      return
    }
    resetForNewPage(state)
    state.requestId = readRequestId()
    startImpressionObserver(state)
    emitPageView(state)
  }, [pathname])
}

// Immediate flush for callers that queue an event inside a `pagehide` handler
// (the popup abandon event). The engine's own terminal flushes — the
// visibilitychange-hidden and beforeunload listeners above — have already run
// by pagehide time, so without this the event would die in the queue when the
// tab closes.
export const flushAnalyticsNow = () => {
  if (moduleState) {
    flushBeacon(moduleState, true)
  }
}

export const trackCustomEvent = (
  event: string,
  eventType: EventType,
  properties: Record<string, string | number | boolean | undefined>
) => {
  const state = moduleState
  if (!state) {
    return
  }
  const tracked: TrackedEvent = {
    event,
    eventType,
    properties: {
      ...normalizeTrackingProperties(properties, { eventName: event }),
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    },
    timestamp: Date.now(),
  }
  processEvent(tracked, state)
}

export const emitErrorFromBoundary = (
  message: string,
  extra: Record<string, string | undefined>,
  stateRef: React.RefObject<EngineState | null>
) => {
  const state = stateRef.current
  if (state) {
    emitError(message, extra, state)
  }
}
